// packages import
import { yupResolver } from '@hookform/resolvers/yup'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import EditIcon from '@mui/icons-material/Edit'
import { isValid } from 'date-fns'
import SaveIcon from '@mui/icons-material/Save'
import {
  Box,
  Button,
  DialogActions,
  Divider,
  FormControl,
  Grid,
  IconButton,
  TextField,
  Typography
} from '@mui/material'
import Collapse from '@mui/material/Collapse'
import { FC, useCallback, useContext, useEffect, useState } from 'react'
import { Controller, FormProvider, SubmitHandler, useForm } from 'react-hook-form'
// component import
import { Alert } from '../common/Alert'
import { CustomController } from '../common/CustomController'
import { CustomModal } from '../common/CustomModal'
import { EditExternalUserAddress } from './EditExternalUserAddress'
import { InputFormFields } from './InputFormFields'
// others
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import moment from 'moment'
import {
  CaseStatus,
  ExternalUser,
  Maybe,
  UpdateExternalUserInput,
  useUpdateExternalUserMutation
} from '../../../generated'
import { CANCEL, CLIENT_ADDRESS, GENERAL_INFORMATION, NOT_APPLICABLE, STANDARD_DATE_FORMAT } from '../../constants'
import { CaseContext } from '../../context'
import { AppContext } from '../../context/AppContext'
import { ActionType } from '../../context/AppContextReducer'
import { EditExternalUserModalProps } from '../../interfaceTypes'
import { returnErrorMessage } from '../../utils'
import { updateClientValidationSchema } from '../../validationSchema'
import { CommonExternalUserView, renderFieldData } from '../common/CommonExternalUserView'
import { ExternalUserActionType } from './externalUserReducer'

export const EditClient: FC<EditExternalUserModalProps> = ({
  userDetails,
  refetch,
  contactRefetch: singleExternalRefetch,
  caseRefetch,
  dispatch: externalUserDispatch,
  state,
  isEditOpen: defaultEditOpen,
  clientRefetch
}): JSX.Element => {
  const { dispatch, isEdit } = useContext(AppContext)
  const userDetailTest = userDetails && userDetails
  const [userDetail, setUserDetail] = useState<Maybe<ExternalUser>>(userDetailTest!)
  const {
    firstName,
    middleName,
    lastName,
    businessName,
    suffix,
    initials,
    dateOfBirth,
    email,
    phoneNumber,
    homePhone,
    personalPhone,
    addresses,
    contractBalance,
    clientNumber,
    id,
    arrearsBalance,
    residualBalance,
    trustBalance,
    lastPayDate,
    lastPayAmount,
    socialSecurityNumber,
    driverLicenseNumber,
    isInternational,
    language
  } = userDetail || {}
  const { id: addressId } = (addresses && addresses[0]) || {}
  const [isEditOpen, setIsEditOpen] = useState<boolean>(defaultEditOpen ?? false)
  const phoneNumberWithOutCountryCode = phoneNumber?.at(0) === '+' ? phoneNumber?.substring(2) : phoneNumber
  const { currentCase } = useContext(CaseContext)
  const { status: caseStatus } = currentCase || {}
  const isClosed = caseStatus === CaseStatus.Completed

  useEffect(() => {
    setUserDetail(userDetailTest!)
    // eslint-disable-next-line
  }, [userDetails])

  const handleClose = () => {
    isEdit && dispatch({ type: ActionType.SET_IS_EDIT, isEdit: !isEdit })
    refetch && refetch()
    singleExternalRefetch && singleExternalRefetch()
    clientRefetch && clientRefetch()
    caseRefetch && caseRefetch()
    setIsEditOpen(false)
    resetFields()
  }

  const [updateExternalUserMutation, { loading: updateExternalUserLoader }] = useUpdateExternalUserMutation({
    onError({ message }) {
      returnErrorMessage(message)
    },

    onCompleted(data) {
      if (data) {
        const {
          updateExternalUser: { response, externalUser }
        } = data || {}

        if (response) {
          const { status } = response

          if (status === 200 && externalUser) {
            Alert.success('Client Updated Successfully')
            externalUserDispatch &&
              externalUserDispatch({
                type: ExternalUserActionType.SET_CURRENT_EXTERNAL_USER,
                currentExternalUser: externalUser
              })
            setUserDetail(externalUser)
            clientRefetch && clientRefetch()
            isEditOpen && setIsEditOpen(false)
            resetFields()
          }
        }
      }
    }
  })

  const methods = useForm<UpdateExternalUserInput>({
    mode: 'all',
    resolver: yupResolver(updateClientValidationSchema)
  })
  const { handleSubmit, reset, control } = methods

  const resetFields = useCallback(() => {
    reset({
      firstName: firstName || '',
      middleName: middleName || '',
      suffix: suffix || '',
      initials: initials || '',
      dateOfBirth: dateOfBirth || '',
      lastName: lastName || '',
      businessName: businessName || '',
      phoneNumber: phoneNumberWithOutCountryCode || '',
      homePhone: homePhone || '',
      personalPhone: personalPhone || '',
      email: email || '',
      clientNumber: clientNumber || '',
      contractBalance: contractBalance || '',
      arrearsBalance: arrearsBalance || '',
      residualBalance: residualBalance || '',
      trustBalance: trustBalance || '',
      lastPayDate: lastPayDate || '',
      lastPayAmount: lastPayAmount || '',
      socialSecurityNumber: socialSecurityNumber || '',
      driverLicenseNumber: driverLicenseNumber || '',
      isInternational,
      language: language || ''
    })
  }, [
    arrearsBalance,
    businessName,
    clientNumber,
    contractBalance,
    dateOfBirth,
    email,
    firstName,
    homePhone,
    initials,
    lastName,
    lastPayAmount,
    lastPayDate,
    middleName,
    personalPhone,
    phoneNumberWithOutCountryCode,
    reset,
    residualBalance,
    suffix,
    trustBalance,
    socialSecurityNumber,
    driverLicenseNumber,
    isInternational,
    language
  ])

  useEffect(() => {
    resetFields()
    // eslint-disable-next-line
  }, [resetFields])

  const onSubmit: SubmitHandler<UpdateExternalUserInput> = async (data: UpdateExternalUserInput) => {
    const { firstName, lastName, dateOfBirth, phoneNumber, homePhone, personalPhone, ...rest } = data

    await updateExternalUserMutation({
      variables: {
        updateExternalUserInput: {
          ...rest,
          lastPayDate: undefined,
          id: id || '',
          dateOfBirth: isValid(new Date(dateOfBirth)) ? dateOfBirth : undefined,
          firstName: firstName?.trim() || '',
          lastName: lastName?.trim() || '',
          phoneNumber: phoneNumber ?? undefined,
          homePhone: homePhone ?? undefined,
          personalPhone: personalPhone ?? undefined
        }
      }
    })
  }

  return (
    <CustomModal title="Client Details" isOpen={isEdit} handleClose={handleClose}>
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Box display="flex" justifyContent="space-between" style={{ alignItems: 'baseline' }}>
            <Typography variant="body2" fontSize="18x" sx={{ fontWeight: 'medium', marginBottom: '0.875rem' }}>
              {GENERAL_INFORMATION}
            </Typography>

            <Box style={{ alignItems: 'baseline' }}>
              <IconButton
                sx={{ p: 0 }}
                color="primary"
                aria-label={isEditOpen ? 'back' : 'edit'}
                size="large"
                disabled={isClosed}
                onClick={() => {
                  setIsEditOpen(!isEditOpen)
                  resetFields()
                }}
              >
                {isEditOpen ? <ArrowBackIcon /> : <EditIcon />}
              </IconButton>

              {isEditOpen && (
                <IconButton color="primary" type="submit" disabled={updateExternalUserLoader} aria-label="save">
                  <SaveIcon />
                </IconButton>
              )}
            </Box>
          </Box>

          <Box marginTop={2} marginBottom={3}>
            <Collapse in={!isEditOpen}>
              <Grid container spacing={2} rowSpacing={2}>
                <CommonExternalUserView externalUser={userDetail as ExternalUser} />

                <Grid item md={4} sm={12} xs={12}>
                  <Typography variant="body2" fontSize="14px">
                    Client No:
                  </Typography>
                  {renderFieldData(`#${clientNumber}`)}
                </Grid>

                <Grid item md={4} sm={12} xs={12}>
                  <Typography variant="body2" fontSize="14px">
                    Lanugage:
                  </Typography>
                  {renderFieldData(!!language ? language : NOT_APPLICABLE)}
                </Grid>

                <Grid item md={4} sm={12} xs={12}>
                  <Typography variant="body2" fontSize="14px">
                    Driver License #
                  </Typography>
                  {renderFieldData(!!driverLicenseNumber ? driverLicenseNumber : NOT_APPLICABLE)}
                </Grid>

                <>
                  <Grid item md={4617} sm={12} xs={12}>
                    <Typography variant="body2" fontSize="14px">
                      Last Pay Date:
                    </Typography>
                    {renderFieldData(lastPayDate ? `${moment(lastPayDate).format(STANDARD_DATE_FORMAT)}` : '--')}
                  </Grid>

                  <Grid item md={4} sm={12} xs={12}>
                    <Typography variant="body2" fontSize="14px">
                      Contract Balance:
                    </Typography>
                    {renderFieldData(`$${contractBalance || 0}`)}
                  </Grid>

                  <Grid item md={4} sm={12} xs={12}>
                    <Typography variant="body2" fontSize="14px">
                      Residual Balance:
                    </Typography>
                    {renderFieldData(`$${residualBalance || 0}`)}
                  </Grid>

                  <Grid item md={4} sm={12} xs={12}>
                    <Typography variant="body2" fontSize="14px">
                      Trust Balance:
                    </Typography>
                    {renderFieldData(`$${trustBalance || 0}`)}
                  </Grid>

                  <Grid item md={4} sm={12} xs={12}>
                    <Typography variant="body2" fontSize="14px">
                      Last Pay Amount:
                    </Typography>
                    {renderFieldData(`$${lastPayAmount || 0}`)}
                  </Grid>

                  <Grid item md={4} sm={12} xs={12}>
                    <Typography variant="body2" fontSize="14px">
                      AR Balance:
                    </Typography>
                    {renderFieldData(`$${arrearsBalance || 0}`)}
                  </Grid>
                </>
              </Grid>
            </Collapse>

            <Collapse in={isEditOpen}>
              <Grid container spacing={2} rowSpacing={2}>
                <>
                  <InputFormFields isClient />
                  <Grid item md={6} sm={12} xs={12}>
                    <CustomController
                      controllerName="socialSecurityNumber"
                      controllerLabel="Social Security#"
                      fieldType="text"
                    />
                  </Grid>

                  <Grid item md={6} sm={12} xs={12}>
                    <CustomController
                      controllerName="driverLicenseNumber"
                      controllerLabel="Driver License#"
                      fieldType="text"
                    />
                  </Grid>
                </>

                <Grid item md={12} sm={12} xs={12} mt={2}>
                  <Box display="flex" justifyContent="space-between" style={{ alignItems: 'baseline' }}>
                    <Typography variant="body2" fontSize="18x" sx={{ fontWeight: 'medium', marginBottom: '0.875rem' }}>
                      {'Financial Information'}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item md={6} sm={12} xs={12}>
                  <CustomController
                    controllerName="clientNumber"
                    controllerLabel="Client Number"
                    fieldType="text"
                    isDisabled
                  />
                </Grid>

                <>
                  <Grid item md={6} sm={12} xs={12}>
                    <FormControl variant="outlined" fullWidth sx={{ marginTop: 1 }}>
                      <Controller
                        name="lastPayDate"
                        control={control}
                        render={({ field: { onChange, value }, fieldState: { error, invalid } }) => (
                          <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <DatePicker
                              disablePast
                              label="Last Pay Date"
                              value={value}
                              readOnly
                              onChange={value => {
                                onChange(value)
                              }}
                              renderInput={params => (
                                <TextField
                                  {...params}
                                  disabled
                                  error={invalid}
                                  helperText={invalid ? error?.message : ''}
                                  fullWidth
                                />
                              )}
                            />
                          </LocalizationProvider>
                        )}
                      />
                    </FormControl>
                  </Grid>

                  <Grid item md={6} sm={12} xs={12}>
                    <CustomController
                      controllerName="contractBalance"
                      controllerLabel="Contract Balance"
                      fieldType="number"
                      dollarAdornment
                      isDisabled
                    />
                  </Grid>

                  <Grid item md={6} sm={12} xs={12}>
                    <CustomController
                      controllerName="residualBalance"
                      controllerLabel="Residual Balance"
                      fieldType="number"
                      dollarAdornment
                      isDisabled
                    />
                  </Grid>

                  <Grid item md={6} sm={12} xs={12}>
                    <CustomController
                      controllerName="trustBalance"
                      controllerLabel="Trust Balance"
                      fieldType="number"
                      dollarAdornment
                      isDisabled
                    />
                  </Grid>
                  <Grid item md={6} sm={12} xs={12}>
                    <CustomController
                      controllerName="lastPayAmount"
                      controllerLabel="Last Pay Amount"
                      fieldType="number"
                      dollarAdornment
                      isDisabled
                    />
                  </Grid>

                  <Grid item md={6} sm={12} xs={12}>
                    <CustomController
                      controllerName="arrearsBalance"
                      controllerLabel="AR Balance"
                      fieldType="text"
                      dollarAdornment
                      isDisabled
                    />
                  </Grid>
                </>
              </Grid>
            </Collapse>
          </Box>
        </form>
      </FormProvider>

      <>
        <Divider />

        <Box marginTop={2}>
          <EditExternalUserAddress
            externalUserId={id || ''}
            addressId={addressId || ''}
            addresses={addresses || []}
            refetch={refetch}
            resetFields={resetFields}
            externalUserTitle={CLIENT_ADDRESS}
            handleClose={handleClose}
            externalUserState={state}
          />
        </Box>
      </>

      <DialogActions sx={{ padding: '30px 0 0' }}>
        <Button
          color="primary"
          variant="contained"
          onClick={() => {
            handleClose()
          }}
          aria-label={CANCEL}
        >
          {CANCEL}
        </Button>
      </DialogActions>
    </CustomModal>
  )
}
